import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api-client';
import { Select } from '@/components/shared/select';
import { StatusBadge } from '@/components/shared/status-badge';
import type { Model, ModelBackend, ModelInput } from '@/lib/types';

const BACKEND_LABELS: Record<ModelBackend, string> = {
  openai_direct: 'OpenAI (közvetlen)',
  azure_foundry: 'Azure Foundry',
};

interface FormValues {
  slug: string;
  displayName: string;
  backend: ModelBackend;
  providerModelId: string;
  apiKey: string;
  baseUrl: string;
  azureEndpoint: string;
  azureDeployment: string;
  apiVersion: string;
  enabled: boolean;
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function Models(): JSX.Element {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Model | null>(null);

  const models = useQuery({
    queryKey: ['/api/admin/models'],
    queryFn: () => api.get<Model[]>('/api/admin/models'),
  });

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: ['/api/admin/models'] });
  };

  const create = useMutation({
    mutationFn: (v: ModelInput) => api.post<Model>('/api/admin/models', v),
    onSuccess: () => {
      invalidate();
      setShowForm(false);
    },
  });
  const update = useMutation({
    mutationFn: ({ id, v }: { id: string; v: ModelInput }) =>
      api.patch<Model>(`/api/admin/models/${id}`, v),
    onSuccess: () => {
      invalidate();
      setEditing(null);
    },
  });
  const setEnabled = useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      api.patch<Model>(`/api/admin/models/${id}`, { enabled }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/models/${id}`),
    onSuccess: invalidate,
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Models</h2>
        <button
          className="btn-primary"
          onClick={() => {
            setEditing(null);
            setShowForm((v) => !v);
          }}
        >
          <Plus className="h-4 w-4" /> {showForm ? 'Bezárás' : 'Új model'}
        </button>
      </div>

      {(showForm || editing) && (
        <ModelForm
          key={editing?.id ?? 'new'}
          initial={editing}
          onCancel={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSubmit={(v) => {
            if (editing) update.mutate({ id: editing.id, v });
            else create.mutate(v);
          }}
        />
      )}

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-2.5">Slug</th>
              <th className="px-4 py-2.5">Megjelenítési név</th>
              <th className="px-4 py-2.5">Backend</th>
              <th className="px-4 py-2.5">Provider model ID</th>
              <th className="px-4 py-2.5">Állapot</th>
              <th className="w-24 px-4 py-2.5">Műveletek</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {models.isLoading && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Betöltés…</td></tr>
            )}
            {models.isError && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-rose-600">Hiba: {String(models.error)}</td></tr>
            )}
            {models.data?.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-6 text-center text-slate-500">Nincs model.</td></tr>
            )}
            {models.data?.map((m) => (
              <tr key={m.id} className="hover:bg-slate-50">
                <td className="px-4 py-2.5 font-mono text-xs text-slate-600">{m.slug}</td>
                <td className="px-4 py-2.5 font-medium">{m.displayName}</td>
                <td className="px-4 py-2.5">{BACKEND_LABELS[m.backend]}</td>
                <td className="px-4 py-2.5 font-mono text-xs text-slate-500">{m.providerModelId}</td>
                <td className="px-4 py-2.5">
                  <button
                    title={m.enabled ? 'Kattints a letiltáshoz' : 'Kattints az engedélyezéshez'}
                    onClick={() => setEnabled.mutate({ id: m.id, enabled: !m.enabled })}
                  >
                    <StatusBadge
                      label={m.enabled ? 'Engedélyezve' : 'Letiltva'}
                      tone={m.enabled ? 'green' : 'slate'}
                    />
                  </button>
                </td>
                <td className="px-4 py-2.5">
                  <div className="flex items-center gap-1">
                    <button
                      title="Szerkeszt"
                      className="rounded p-1 text-slate-500 hover:bg-slate-100"
                      onClick={() => { setShowForm(false); setEditing(m); }}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      title="Töröl"
                      className="rounded p-1 text-rose-500 hover:bg-rose-50"
                      onClick={() => {
                        if (confirm(`Törlés: "${m.displayName}"?`)) remove.mutate(m.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ModelForm({
  initial,
  onSubmit,
  onCancel,
}: {
  initial?: Model | null;
  onSubmit: (v: ModelInput) => void;
  onCancel: () => void;
}): JSX.Element {
  const isEdit = Boolean(initial);
  const { register, handleSubmit, control, watch } = useForm<FormValues>({
    defaultValues: initial
      ? {
          slug: initial.slug,
          displayName: initial.displayName,
          backend: initial.backend,
          providerModelId: initial.providerModelId,
          apiKey: '',
          baseUrl: initial.baseUrl ?? '',
          azureEndpoint: initial.azureEndpoint ?? '',
          azureDeployment: initial.azureDeployment ?? '',
          apiVersion: initial.apiVersion ?? '',
          enabled: initial.enabled,
        }
      : {
          slug: '',
          displayName: '',
          backend: 'openai_direct',
          providerModelId: '',
          apiKey: '',
          baseUrl: '',
          azureEndpoint: '',
          azureDeployment: '',
          apiVersion: '2024-10-21',
          enabled: true,
        },
  });

  const backend = watch('backend');
  const isAzure = backend === 'azure_foundry';

  const submit = (v: FormValues): void => {
    const out: ModelInput = {
      slug: (v.slug || slugify(v.providerModelId)).trim(),
      displayName: (v.displayName || v.providerModelId).trim(),
      backend: v.backend,
      providerModelId: v.providerModelId.trim(),
      enabled: Boolean(v.enabled),
    };
    if (v.apiKey) out.apiKey = v.apiKey; // blank on edit → keep stored key
    if (isAzure) {
      out.azureEndpoint = v.azureEndpoint.trim();
      out.azureDeployment = v.azureDeployment.trim();
      if (v.apiVersion) out.apiVersion = v.apiVersion.trim();
    } else if (v.baseUrl) {
      out.baseUrl = v.baseUrl.trim();
    }
    onSubmit(out);
  };

  const keyPlaceholder = initial?.apiKeySet
    ? '•••••••• (tárolva — hagyd üresen a megtartáshoz)'
    : isAzure
      ? 'Azure API kulcs'
      : 'sk-...';

  return (
    <form className="card space-y-3 p-4" onSubmit={handleSubmit(submit)}>
      <div className="text-sm font-semibold text-slate-700">
        {isEdit ? `Model szerkesztése: ${initial?.displayName}` : 'Új model'}
      </div>

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <label className="label">Backend</label>
          <Controller
            control={control}
            name="backend"
            render={({ field }) => (
              <Select
                value={field.value}
                onChange={(v) => field.onChange(v as ModelBackend)}
                options={[
                  { value: 'openai_direct', label: BACKEND_LABELS.openai_direct },
                  { value: 'azure_foundry', label: BACKEND_LABELS.azure_foundry },
                ]}
              />
            )}
          />
        </div>
        <div>
          <label className="label">Model név {isAzure && '(provider-oldali)'}</label>
          <input
            className="input"
            placeholder={isAzure ? 'pl. gpt-4o' : 'pl. gpt-5.5'}
            {...register('providerModelId', { required: true })}
          />
        </div>
      </div>

      {/* OpenAI direct: csak API kulcs (+ opcionális base URL) */}
      {!isAzure && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <label className="label">API kulcs</label>
            <input
              type="password"
              className="input font-mono text-xs"
              placeholder={keyPlaceholder}
              {...register('apiKey', { required: !isEdit })}
            />
          </div>
          <div>
            <label className="label">Base URL (opcionális)</label>
            <input
              className="input"
              placeholder="alapértelmezett: api.openai.com"
              {...register('baseUrl')}
            />
          </div>
        </div>
      )}

      {/* Azure Foundry: endpoint + azure deployment + api version + api key */}
      {isAzure && (
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div>
            <label className="label">Endpoint</label>
            <input
              className="input"
              placeholder="https://<resource>.openai.azure.com"
              {...register('azureEndpoint', { required: true })}
            />
          </div>
          <div>
            <label className="label">Azure deployment név</label>
            <input
              className="input"
              placeholder="az Azure deployment neve"
              {...register('azureDeployment', { required: true })}
            />
          </div>
          <div>
            <label className="label">API verzió</label>
            <input className="input" placeholder="2024-10-21" {...register('apiVersion')} />
          </div>
          <div>
            <label className="label">API kulcs</label>
            <input
              type="password"
              className="input font-mono text-xs"
              placeholder={keyPlaceholder}
              {...register('apiKey', { required: !isEdit })}
            />
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <div>
          <label className="label">Megjelenítési név (opcionális)</label>
          <input className="input" placeholder="alapértelmezett: a model név" {...register('displayName')} />
        </div>
        <div>
          <label className="label">Slug (opcionális)</label>
          <input className="input font-mono text-xs" placeholder="auto a model névből" {...register('slug')} />
        </div>
      </div>

      <label className="flex items-center gap-2 text-sm text-slate-700">
        <input type="checkbox" className="h-4 w-4" {...register('enabled')} />
        Engedélyezve
      </label>
      <div className="flex justify-end gap-2">
        <button type="button" className="btn-secondary" onClick={onCancel}>Mégse</button>
        <button type="submit" className="btn-primary"><Plus className="h-4 w-4" /> Mentés</button>
      </div>
    </form>
  );
}
